import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "../components/pageList";
import { TextInput, Select, Switch, NumberInput } from "@mantine/core";

export const FUND_AMOUNT_FIELDS = [
  "minSubscriptionAmount",
  "minSubsequentInvestment",
  "minHolding",
  "minRedemptionAmount",
];

export const INVESTMENT_HORIZON_OPTIONS = [
  { value: "Short", label: "Short" },
  { value: "Medium", label: "Medium" },
  { value: "Long", label: "Long" },
];

export const RISK_RATING_OPTIONS = ["1", "2", "3", "4", "5"].map((value) => ({
  value,
  label: value,
}));

export const ASSET_CLASS_OPTIONS = [
  { value: "Equity", label: "Equity" },
  { value: "Fixed Income", label: "Fixed Income" },
  { value: "Mixed Assets", label: "Mixed Assets" },
  { value: "Money Market", label: "Money Market" },
];

export const FUND_CURRENCY_OPTIONS = [
  "AUD",
  "CAD",
  "CNY",
  "EUR",
  "GBP",
  "HKD",
  "JPY",
  "SGD",
  "USD",
].map((value) => ({ value, label: value }));

export const DIVIDEND_FREQUENCY_OPTIONS = [
  { value: "Based on D. Pref. in acc.", label: "Based on D. Pref. in acc." },
  { value: "Cash", label: "Cash" },
  { value: "Reinvest", label: "Reinvest" },
];

export const BOOLEAN_FUND_FIELDS = [
  { field: "highYieldBondFund", label: "High Yield Bond Fund" },
  { field: "complexProduct", label: "Complex Product" },
  { field: "derivativesFund", label: "Derivatives Fund" },
  { field: "cies", label: "CIES" },
  { field: "newCies", label: "New CIES" },
  { field: "buyEnabledIfastCentral", label: "Buy Enabled - iFAST Central" },
  { field: "buyEnabledIgp", label: "Buy Enabled - iGP" },
  { field: "rspAvailability", label: "RSP Availability" },
];

const SECTION_PROPS = {
  minHeight: 0,
  withPaper: false,
  withBorder: false,
  mt: "sm",
  mb: "xs",
};

const GRID_COLS = (prefix) => [
  { key: `${prefix}-g1`, props: { span: 6 } },
  { key: `${prefix}-g2`, props: { span: 6 } },
];

export const parseFundAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;

  const withoutCurrency = String(value)
    .trim()
    .replace(/\s*\([A-Za-z]{3}\)\s*$/i, "")
    .replace(/,/g, "")
    .replace(/\s+/g, "");

  if (!withoutCurrency) return null;

  const parsed = Number(withoutCurrency);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatFundAmount = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = typeof value === "number" ? value : parseFundAmount(value);
  if (numeric === null) return "--";
  return numeric.toLocaleString();
};

export const stripInternalFundFields = (fund = {}) => {
  const { __sourceIndex, ...rest } = fund;
  return rest;
};

export const attachSourceIndexes = (funds = []) =>
  funds.map((fund, index) => ({ ...fund, __sourceIndex: index }));

const selectField = (name, label, data, parent, extra = {}) => ({
  name,
  parent,
  component: Select,
  props: {
    label,
    data,
    searchable: true,
    clearable: true,
    ...extra,
  },
});

const amountField = (name, label, parent) => ({
  name,
  parent,
  component: NumberInput,
  props: {
    label,
    min: 0,
    precision: 0,
    thousandSeparator: ",",
    hideControls: true,
    placeholder: "0",
  },
});

const switchField = (name, label, parent) => ({
  name,
  parent,
  component: Switch,
  props: {
    label,
    size: "sm",
    type: "checkbox",
  },
});

export const buildFundEditFormSchema = () => ({
  title: "Edit Fund",
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "xl",
  },
  initialValues: {
    isinCode: "",
    fundName: "",
    ifastCode: "",
    investmentHorizon: null,
    riskRating: null,
    trailerRebate: "",
    assetClass: null,
    highYieldBondFund: false,
    fundCurrency: null,
    minSubscriptionAmount: null,
    minSubsequentInvestment: null,
    minHolding: null,
    minRedemptionAmount: null,
    dividendFrequency: null,
    complexProduct: false,
    derivativesFund: false,
    cies: false,
    newCies: false,
    buyEnabledIfastCentral: false,
    buyEnabledIgp: false,
    rspAvailability: false,
  },
  layout: {
    containers: [
      {
        key: "identification-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          ...SECTION_PROPS,
          title: "Identification",
          description: "Core fund identifiers",
        },
      },
      {
        key: "identification-grid",
        parent: "identification-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: GRID_COLS("identification"),
      },
      {
        key: "classification-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          ...SECTION_PROPS,
          title: "Classification",
          description: "Risk profile and product category",
        },
      },
      {
        key: "classification-grid",
        parent: "classification-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: GRID_COLS("classification"),
      },
      {
        key: "amounts-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          ...SECTION_PROPS,
          title: "Minimum Amounts",
          description: "Numeric amounts in the fund currency selected above",
        },
      },
      {
        key: "amounts-grid",
        parent: "amounts-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: GRID_COLS("amounts"),
      },
      {
        key: "flags-section",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          ...SECTION_PROPS,
          title: "Product Flags",
          description: "Availability and regulatory attributes",
        },
      },
      {
        key: "flags-grid",
        parent: "flags-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: GRID_COLS("flags"),
      },
    ],
    fields: [
      {
        name: "isinCode",
        parent: "identification-g1",
        component: TextInput,
        props: { label: "ISIN Code", placeholder: "LU0000000000" },
      },
      {
        name: "ifastCode",
        parent: "identification-g2",
        component: TextInput,
        props: { label: "iFAST Code", placeholder: "HKXXXXXX" },
      },
      {
        name: "fundName",
        parent: "identification-section",
        component: TextInput,
        props: {
          label: "Name of the fund",
          placeholder: "Fund name",
        },
      },
      selectField(
        "investmentHorizon",
        "Investment Horizon",
        INVESTMENT_HORIZON_OPTIONS,
        "classification-g1"
      ),
      selectField("riskRating", "Risk Rating", RISK_RATING_OPTIONS, "classification-g2", {
        searchable: false,
      }),
      selectField("assetClass", "Asset Class", ASSET_CLASS_OPTIONS, "classification-g1"),
      selectField("fundCurrency", "Fund Currency", FUND_CURRENCY_OPTIONS, "classification-g2"),
      {
        name: "trailerRebate",
        parent: "classification-g1",
        component: TextInput,
        props: { label: "Trailer Rebate", placeholder: "0.1" },
      },
      selectField(
        "dividendFrequency",
        "Dividend Frequency",
        DIVIDEND_FREQUENCY_OPTIONS,
        "classification-g2"
      ),
      amountField("minSubscriptionAmount", "Min Subscription Amount", "amounts-g1"),
      amountField(
        "minSubsequentInvestment",
        "Minimum Subsequent Investment",
        "amounts-g2"
      ),
      amountField("minHolding", "Minimum Holding", "amounts-g1"),
      amountField("minRedemptionAmount", "Minimum Redemption Amount", "amounts-g2"),
      ...BOOLEAN_FUND_FIELDS.slice(0, 4).map(({ field, label }) =>
        switchField(field, label, "flags-g1")
      ),
      ...BOOLEAN_FUND_FIELDS.slice(4).map(({ field, label }) =>
        switchField(field, label, "flags-g2")
      ),
    ],
  },
});
